<template>
  <v-card flat>
    <v-card-item>
      <template v-slot:prepend>
        <v-avatar color="secondary" size="small">
          <v-icon icon="mdi-format-list-group"></v-icon>
        </v-avatar>
      </template>
      <v-card-title class="text-subtitle-1 text-primary">
        {{ $t("strings.title-kpi-values") }}
      </v-card-title>
      <v-card-subtitle class="text-caption">
        {{ $t("strings.values-definition-hint") }}
        <v-tooltip activator="parent" location="top">
          {{ $t("strings.values-definition-hint") }}
        </v-tooltip>
      </v-card-subtitle>
    </v-card-item>

    <v-card-text class="pt-2 pb-1">
      <v-row class="d-flex align-center" dense>
        <v-col cols="9">
          <template v-if="valueType === 'QUALITATIVE'">
            <v-autocomplete v-model="newValue" :items="Array.isArray(allowedValues) ? allowedValues : []"
              :label="$t('strings.add-value')" :placeholder="$t('strings.add-value')" dense hide-details
              variant="outlined" single-line density="compact" @keyup.enter="addValue" />
          </template>
          <template v-else>
            <v-text-field v-model="newValue" :label="$t('strings.add-value')" :type="inputType" dense hide-details
              variant="outlined" single-line density="compact" @keyup.enter="addValue" />
          </template>
        </v-col>
        <v-col cols="auto">
          <v-btn class="mr-2" color="secondary" rounded="xl" size="x-small" variant="outlined" icon="mdi-plus"
            @click="addValue" :disabled="addingDisabled" :title="$t('strings.add-value')">
          </v-btn>

          <v-btn color="secondary" rounded="xl" size="x-small" variant="outlined" icon="mdi-delete-sweep"
            @click="requestClearAll" :disabled="!values.length" :title="$t('labels.clear-all')">
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text class="mt-4 pt-0 pb-1">
      <v-row dense>
        <v-col cols="12">
          <v-virtual-scroll :items="values" item-height="56" height="220" class="pa-0">
            <template #default="{ item, index }">
              <div class="px-1 py-1">
                <v-list-item variant="outlined" :rounded="true" density="compact" class="mb-1">
                  <v-list-item-title>{{ item.value }}</v-list-item-title>
                  <template v-slot:append>
                    <v-btn color="primary" density="compact" variant="text" icon="mdi-delete"
                      @click="requestRemove(index)"></v-btn>
                  </template>
                </v-list-item>
              </div>
              <v-divider v-if="index < values.length - 1" class="pb-1" />
            </template>
          </v-virtual-scroll>
        </v-col>
      </v-row>
    </v-card-text>
    <v-divider></v-divider>
  </v-card>

  <!-- Confirm dialogs -->
  <ConfirmDialog v-model="showConfirmRemove" :message="$t('strings.confirm-message', {
    name:
      (values[pendingRemoveIndex] && values[pendingRemoveIndex].value) ||
      '',
  })
    " @confirm="confirmRemove" @cancel="cancelRemove" />

  <ConfirmDialog v-model="showConfirmClear" :message="$t('strings.values-clear-all-confirm') ||
    $t('strings.confirm-message', { name: '' })
    " @confirm="confirmClearAll" @cancel="cancelClearAll" />
</template>

<script>
import ConfirmDialog from './ConfirmDialog.vue'

export default {
  name: "KpiValuesInput",
  components: {
    ConfirmDialog,
  },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    // valueType controls whether input is QUALITATIVE or QUANTITATIVE
    valueType: {
      type: String,
      default: "QUANTITATIVE",
    },
    // when provided and valueType is 'string', only these values are allowed
    allowedValues: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      newValue: "",
      // confirm dialog state
      pendingRemoveIndex: null,
      showConfirmRemove: false,
      showConfirmClear: false,
    };
  },
  computed: {
    addingDisabled() {
      const val = this.newValue == null ? "" : String(this.newValue).trim();
      // when QUALITATIVE, ensure value is in allowedValues
      if (this.valueType === "QUALITATIVE") {
        const allowed = Array.isArray(this.allowedValues)
          ? this.allowedValues.map((v) => String(v))
          : [];
        return !val || !allowed.includes(val);
      }
      // QUANTITATIVE: require non-empty and valid number
      if (val === "") return true;
      const parsed = Number(val);
      if (Number.isNaN(parsed)) return true;
      return false;
    },
    values: {
      get() {
        // normalize incoming modelValue to array of {value,timestamp}
        if (!Array.isArray(this.modelValue)) return [];
        return this.modelValue.map((it) => {
          if (it && typeof it === "object" && "value" in it) return it;
          return { value: it, timestamp: null };
        });
      },
      set(v) {
        // emit array of objects
        this.$emit("update:modelValue", v);
      },
    },
    inputType() {
      return this.valueType === "QUALITATIVE" ? "text" : "number";
    },
  },
  methods: {
    addValue() {
      const trimmed = String(this.newValue).trim();
      if (trimmed === "") return;
      let valueToPush;
      if (this.valueType === "QUALITATIVE") {
        const allowed = Array.isArray(this.allowedValues)
          ? this.allowedValues.map((v) => String(v))
          : [];
        if (!allowed.includes(trimmed)) {
          this.newValue = "";
          return;
        }
        valueToPush = { value: trimmed, timestamp: new Date().toISOString() };
      } else {
        const parsed = Number(trimmed);
        if (Number.isNaN(parsed)) return;
        valueToPush = { value: parsed, timestamp: new Date().toISOString() };
      }
      const newList = [...this.values, valueToPush];
      this.values = newList;
      this.newValue = "";
    },
    removeAt(index) {
      if (!Array.isArray(this.values)) return;
      const newList = [...this.values];
      newList.splice(index, 1);
      this.values = newList;
    },
    requestRemove(index) {
      this.pendingRemoveIndex = index;
      this.showConfirmRemove = true;
    },
    confirmRemove() {
      if (this.pendingRemoveIndex == null) {
        this.showConfirmRemove = false;
        return;
      }
      this.removeAt(this.pendingRemoveIndex);
      this.pendingRemoveIndex = null;
      this.showConfirmRemove = false;
    },
    cancelRemove() {
      this.pendingRemoveIndex = null;
      this.showConfirmRemove = false;
    },
    clearAll() {
      this.values = [];
    },
    requestClearAll() {
      this.showConfirmClear = true;
    },
    confirmClearAll() {
      this.clearAll();
      this.showConfirmClear = false;
    },
    cancelClearAll() {
      this.showConfirmClear = false;
    },
  },
};
</script>

<style scoped>
.v-list-item-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
